<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'dunningLetter',
    path: '/dunning-letters/:id',
    component: 'DunningLetter',
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'DunningLetter',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    id: {
      type: String,
      required: true,
      default: null,
    },
    payload: {
      type: Object,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const sendMailDropdown = ref(null);
  const generatePdfDropdown = ref(null);

  const $refs = {
    get sendMailDropdown() {
      return sendMailDropdown.value;
    },
    get generatePdfDropdown() {
      return generatePdfDropdown.value;
    },
  };

  const item = ref(null);
  const templates = ref(null);
  const emailTemplates = ref(null);
  const htmlTemplates = ref(null);
  const sendmail = ref(null);
  const printdoc = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'xl',
  }));
  const b_1024078196022266_style = computed(() => ({
    minWidth: '250px',
  }));
  const b_3061418626715996_style = computed(() => ({
    minWidth: '250px',
  }));
  const b_3273883411533724_options = computed(() => [
    {
      value: null,
      text: '-',
    },
    {
      value: 'sent',
      text: $fn.value.pgettext('DunningLetterStatus', 'Sent'),
    },
    {
      value: 'delivered',
      text: $fn.value.pgettext('DunningLetterStatus', 'Delivered'),
    },
    {
      value: 'error',
      text: $fn.value.pgettext('DunningLetterStatus', 'Error'),
    },
  ]);
  const b_2771751501651205_columns = computed(() => [
    {
      name: $fn.value.gettext('Invoice number'),
      field: 'invoice_number',
    },
    {
      name: $fn.value.gettext('Completion date'),
      field: 'invoice_data.fulfillment_date',
      textAlign: 'right',
      type: 'date',
    },
    {
      name: $fn.value.gettext('Due date'),
      field: 'invoice_data.due_date',
      textAlign: 'right',
      type: 'date',
    },
    {
      name: $fn.value.gettext('Amount'),
      field: 'invoice_data.amount',
      textAlign: 'right',
      type: 'number',
    },
    {
      name: $fn.value.gettext('Paid'),
      field: 'invoice_data.paid',
      textAlign: 'right',
      type: 'number',
    },
    {
      name: $fn.value.gettext('Debt'),
      field: 'invoice_data.debt',
      textAlign: 'right',
      type: 'number',
    },
  ]);

  const $data = computed(() => ({
    item: item.value,
    templates: templates.value,
    emailTemplates: emailTemplates.value,
    htmlTemplates: htmlTemplates.value,
    sendmail: sendmail.value,
    printdoc: printdoc.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_1024078196022266_style: b_1024078196022266_style.value,
    b_3061418626715996_style: b_3061418626715996_style.value,
    b_3273883411533724_options: b_3273883411533724_options.value,
    b_2771751501651205_columns: b_2771751501651205_columns.value,
  }));

  const $$load_item = async () => {
    return (
      props.payload ||
      (props.id !== null
        ? $fn.value.fetch('read_dunning_letter', {
            asResource: true,
            parameters: { id: props.id },
          })
        : $fn.value.schemaDefaults('DunningLetter'))
    );
  };
  const $$load_templates = async () => {
    return $fn.value.fetch('list_template');
  };
  const $$load_emailTemplates = async () => {
    return _.filter(templates.value, {
      object_type: 'dunning_letter',
      template_type: 'mjml',
    });
  };
  const $$load_htmlTemplates = async () => {
    return _.filter(templates.value, {
      object_type: 'dunning_letter',
      template_type: 'html',
    });
  };
  const $$load_sendmail = async () => {
    return {
      email: _.isArray(item.value.customer_data.emails)
        ? _.join(item.value.customer_data.emails, '; ')
        : item.value.customer_data.emails,
      template: _.get(_.first(emailTemplates.value), 'id'),
    };
  };
  const $$load_printdoc = async () => {
    return { template: _.get(_.first(htmlTemplates.value), 'id') };
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'templates' },
      { name: 'emailTemplates', dependsOn: ['templates'] },
      { name: 'htmlTemplates', dependsOn: ['templates'] },
      { name: 'sendmail', dependsOn: ['item', 'emailTemplates'] },
      { name: 'printdoc', dependsOn: ['htmlTemplates'] },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_1024078196022266_style', nodeId: '1024078196022266' },
      { name: 'b_3061418626715996_style', nodeId: '3061418626715996' },
      { name: 'b_3273883411533724_options', nodeId: '3273883411533724' },
      { name: 'b_2771751501651205_columns', nodeId: '2771751501651205' },
    ],
    loaders: {
      item: $$load_item,
      templates: $$load_templates,
      emailTemplates: $$load_emailTemplates,
      htmlTemplates: $$load_htmlTemplates,
      sendmail: $$load_sendmail,
      printdoc: $$load_printdoc,
    },
    ux: {},
    dataRefs: {
      item,
      templates,
      emailTemplates,
      htmlTemplates,
      sendmail,
      printdoc,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_1024078196022266_style,
      b_3061418626715996_style,
      b_3273883411533724_options,
      b_2771751501651205_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          item.value.snapshot('reference_number') ||
            $fn.value.gettext('Dunning Letter'),
        ],
        icon: $config.value.dunningLetter.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.id,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('id');
    }
  );
  watch(
    () => props.payload,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('payload');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_iymm4l5k3_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2996188254223402_closeComponent_j5g8t0i5y_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
      },
    };
  };
  const act_2996188254223402_reloadSlickgrid_lm7ql0q3o_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'all',
    };
  };
  const act_2996188254223402_request_oh4tvbsmy_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_dunning_letter',
      data: item.value,
    };
  };
  const act_2996188254223402_request_oh4tvbsmy_getConfirm = async ($event) => {
    return {
      message: $fn.value.gettext('Please confirm delete.'),
      btnOk: $fn.value.gettext('Delete'),
    };
  };
  const act_5048434852698054_script_ogfv9hegt_value_callable = async (
    $event
  ) => {
    return $refs.sendMailDropdown.hide(true);
  };
  const act_5048434852698054_script_ogfv9hegt_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5048434852698054_script_ogfv9hegt_value_callable($event);
      },
    };
  };
  const act_5048434852698054_reloadSlickgrid_2hku05r6a_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'all',
    };
  };
  const act_7525385716635984_script_ro1jo5iyx_value_callable = async (
    $event
  ) => {
    return $refs.generatePdfDropdown.hide(true);
  };
  const act_7525385716635984_script_ro1jo5iyx_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7525385716635984_script_ro1jo5iyx_value_callable($event);
      },
    };
  };
  const act_7525385716635984_reloadSlickgrid_cmsh45ky3_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'all',
    };
  };
  const act_7789867021953041_setData_8rfwk7uqa_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item',
      value: new $fn.value.Resource($event.data.response.data),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const inst_evh_7789867021953041_success_lh6r419cr_getWhen = async (
    $event
  ) => {
    return !!$event.data.response.data;
  };
  const act_7789867021953041_form_5oz26qyij_getActionArgs = async ($event) => {
    return {
      name: 'dunningLetter',
      reset: true,
    };
  };
  const act_7789867021953041_reloadSlickgrid_ce2jrz9gb_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'all',
    };
  };
  const act_7789867021953041_request_znr6ddv1j_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_dunning_letter',
      data: item.value,
    };
  };

  const act_7315092382398562_closeModal_iymm4l5k3 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_iymm4l5k3_getActionArgs,
    });
  const inst_evh_7315092382398562_close_0r234pt5y =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_iymm4l5k3,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_0r234pt5y],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_c11b5kgcf =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_22zsj50tr =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_c11b5kgcf,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_22zsj50tr],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_l8elyd2al =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_rcknwdmjb =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_l8elyd2al,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_rcknwdmjb],
    event: 'close',
  });
  const act_4440454779651433_reloadComponentData_phar86h07 =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reloadData',
    });
  const inst_evh_4440454779651433_clickPreventStop_t2foq3zuy =
    new actionTypes.EventHandlerImpl({
      action: act_4440454779651433_reloadComponentData_phar86h07,
      event: 'click.prevent.stop',
      displayName: 'reloadData',
    });
  const inst_evh_4440454779651433_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4440454779651433_clickPreventStop_t2foq3zuy],
      event: 'click.prevent.stop',
    });
  const act_2996188254223402_closeComponent_j5g8t0i5y =
    new actionTypes.CloseComponentAction({
      actionArgs: act_2996188254223402_closeComponent_j5g8t0i5y_getActionArgs,
    });
  const inst_evh_2996188254223402_success_7kx5brids =
    new actionTypes.EventHandlerImpl({
      action: act_2996188254223402_closeComponent_j5g8t0i5y,
      event: 'success',
      displayName: 'closeComponent',
    });
  const act_2996188254223402_reloadSlickgrid_lm7ql0q3o =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_2996188254223402_reloadSlickgrid_lm7ql0q3o_getActionArgs,
    });
  const inst_evh_2996188254223402_success_5n4k16mcj =
    new actionTypes.EventHandlerImpl({
      action: act_2996188254223402_reloadSlickgrid_lm7ql0q3o,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_2996188254223402_request_oh4tvbsmy = new actionTypes.RequestAction({
    actionArgs: act_2996188254223402_request_oh4tvbsmy_getActionArgs,
    confirm: act_2996188254223402_request_oh4tvbsmy_getConfirm,
    displayName: 'delete',
    events: [
      inst_evh_2996188254223402_success_7kx5brids,
      inst_evh_2996188254223402_success_5n4k16mcj,
    ],
  });
  const inst_evh_2996188254223402_clickPreventStop_fpw0sj6j8 =
    new actionTypes.EventHandlerImpl({
      action: act_2996188254223402_request_oh4tvbsmy,
      event: 'click.prevent.stop',
      displayName: 'delete',
    });
  const inst_evh_2996188254223402_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2996188254223402_clickPreventStop_fpw0sj6j8],
      event: 'click.prevent.stop',
    });
  const act_5048434852698054_script_ogfv9hegt = new actionTypes.ScriptAction({
    actionArgs: act_5048434852698054_script_ogfv9hegt_getActionArgs,
  });
  const inst_evh_5048434852698054_completed_adkygwr95 =
    new actionTypes.EventHandlerImpl({
      action: act_5048434852698054_script_ogfv9hegt,
      event: 'completed',
      displayName: 'script',
    });
  const act_5048434852698054_reloadComponentData_tle4qxihy =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_5048434852698054_completed_c0lxhudky =
    new actionTypes.EventHandlerImpl({
      action: act_5048434852698054_reloadComponentData_tle4qxihy,
      event: 'completed',
      displayName: 'reloadComponentData',
    });
  const act_5048434852698054_reloadSlickgrid_2hku05r6a =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_5048434852698054_reloadSlickgrid_2hku05r6a_getActionArgs,
    });
  const inst_evh_5048434852698054_completed_kefb5b6mw =
    new actionTypes.EventHandlerImpl({
      action: act_5048434852698054_reloadSlickgrid_2hku05r6a,
      event: 'completed',
      displayName: 'reloadSlickgrid',
    });
  const inst_evh_5048434852698054_completed = new actionTypes.EventHandlerGroup(
    {
      handlers: [
        inst_evh_5048434852698054_completed_adkygwr95,
        inst_evh_5048434852698054_completed_c0lxhudky,
        inst_evh_5048434852698054_completed_kefb5b6mw,
      ],
      event: 'completed',
    }
  );
  const act_7525385716635984_script_ro1jo5iyx = new actionTypes.ScriptAction({
    actionArgs: act_7525385716635984_script_ro1jo5iyx_getActionArgs,
  });
  const inst_evh_7525385716635984_completed_gqsgb4uq5 =
    new actionTypes.EventHandlerImpl({
      action: act_7525385716635984_script_ro1jo5iyx,
      event: 'completed',
      displayName: 'script',
    });
  const act_7525385716635984_reloadComponentData_mppepfvaz =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7525385716635984_completed_bsdpwl8lu =
    new actionTypes.EventHandlerImpl({
      action: act_7525385716635984_reloadComponentData_mppepfvaz,
      event: 'completed',
      displayName: 'reloadComponentData',
    });
  const act_7525385716635984_reloadSlickgrid_cmsh45ky3 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_7525385716635984_reloadSlickgrid_cmsh45ky3_getActionArgs,
    });
  const inst_evh_7525385716635984_completed_bv5nbf2dl =
    new actionTypes.EventHandlerImpl({
      action: act_7525385716635984_reloadSlickgrid_cmsh45ky3,
      event: 'completed',
      displayName: 'reloadSlickgrid',
    });
  const inst_evh_7525385716635984_completed = new actionTypes.EventHandlerGroup(
    {
      handlers: [
        inst_evh_7525385716635984_completed_gqsgb4uq5,
        inst_evh_7525385716635984_completed_bsdpwl8lu,
        inst_evh_7525385716635984_completed_bv5nbf2dl,
      ],
      event: 'completed',
    }
  );
  const act_7789867021953041_setData_8rfwk7uqa = new actionTypes.SetDataAction({
    actionArgs: act_7789867021953041_setData_8rfwk7uqa_getActionArgs,
  });
  const inst_evh_7789867021953041_success_lh6r419cr =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_setData_8rfwk7uqa,
      event: 'success',
      when: inst_evh_7789867021953041_success_lh6r419cr_getWhen,
      displayName: 'setData',
    });
  const act_7789867021953041_form_5oz26qyij = new actionTypes.FormAction({
    actionArgs: act_7789867021953041_form_5oz26qyij_getActionArgs,
  });
  const inst_evh_7789867021953041_success_su231jtei =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_form_5oz26qyij,
      event: 'success',
      displayName: 'form',
    });
  const act_7789867021953041_reloadSlickgrid_ce2jrz9gb =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_7789867021953041_reloadSlickgrid_ce2jrz9gb_getActionArgs,
    });
  const inst_evh_7789867021953041_success_rxx70gl94 =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_reloadSlickgrid_ce2jrz9gb,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_7789867021953041_request_znr6ddv1j = new actionTypes.RequestAction({
    actionArgs: act_7789867021953041_request_znr6ddv1j_getActionArgs,
    displayName: 'save',
    events: [
      inst_evh_7789867021953041_success_lh6r419cr,
      inst_evh_7789867021953041_success_su231jtei,
      inst_evh_7789867021953041_success_rxx70gl94,
    ],
  });
  const inst_evh_7789867021953041_submit_zu8rz54ij =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_request_znr6ddv1j,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_7789867021953041_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7789867021953041_submit_zu8rz54ij],
    event: 'submit',
  });
  const act_2140198529698299_closeComponent_7b3ati72v =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2140198529698299_clickPreventStop_5zxqkyh1s =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeComponent_7b3ati72v,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_5zxqkyh1s],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_0r234pt5y,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_22zsj50tr,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_rcknwdmjb,
    inst_evh_8564662037462133_close,
    inst_evh_4440454779651433_clickPreventStop_t2foq3zuy,
    reloadData: inst_evh_4440454779651433_clickPreventStop_t2foq3zuy,
    inst_evh_4440454779651433_clickPreventStop,
    inst_evh_2996188254223402_success_7kx5brids,
    inst_evh_2996188254223402_success_5n4k16mcj,
    inst_evh_2996188254223402_clickPreventStop_fpw0sj6j8,
    delete: inst_evh_2996188254223402_clickPreventStop_fpw0sj6j8,
    inst_evh_2996188254223402_clickPreventStop,
    inst_evh_5048434852698054_completed_adkygwr95,
    inst_evh_5048434852698054_completed_c0lxhudky,
    inst_evh_5048434852698054_completed_kefb5b6mw,
    inst_evh_5048434852698054_completed,
    inst_evh_7525385716635984_completed_gqsgb4uq5,
    inst_evh_7525385716635984_completed_bsdpwl8lu,
    inst_evh_7525385716635984_completed_bv5nbf2dl,
    inst_evh_7525385716635984_completed,
    inst_evh_7789867021953041_success_lh6r419cr,
    inst_evh_7789867021953041_success_su231jtei,
    inst_evh_7789867021953041_success_rxx70gl94,
    inst_evh_7789867021953041_submit_zu8rz54ij,
    save: inst_evh_7789867021953041_submit_zu8rz54ij,
    inst_evh_7789867021953041_submit,
    inst_evh_2140198529698299_clickPreventStop_5zxqkyh1s,
    inst_evh_2140198529698299_clickPreventStop,
  };

  const evh_7315092382398562_close_0r234pt5y = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_0r234pt5y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_22zsj50tr = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_22zsj50tr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_rcknwdmjb = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_rcknwdmjb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4440454779651433_clickPreventStop_t2foq3zuy = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4440454779651433_clickPreventStop_t2foq3zuy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4440454779651433_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4440454779651433_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2996188254223402_success_7kx5brids = async (event, scope) => {
    return await actions.inst_evh_2996188254223402_success_7kx5brids.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2996188254223402_success_5n4k16mcj = async (event, scope) => {
    return await actions.inst_evh_2996188254223402_success_5n4k16mcj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2996188254223402_clickPreventStop_fpw0sj6j8 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2996188254223402_clickPreventStop_fpw0sj6j8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2996188254223402_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2996188254223402_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5048434852698054_completed_adkygwr95 = async (event, scope) => {
    return await actions.inst_evh_5048434852698054_completed_adkygwr95.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5048434852698054_completed_c0lxhudky = async (event, scope) => {
    return await actions.inst_evh_5048434852698054_completed_c0lxhudky.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5048434852698054_completed_kefb5b6mw = async (event, scope) => {
    return await actions.inst_evh_5048434852698054_completed_kefb5b6mw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5048434852698054_completed = async (event, scope) => {
    return await actions.inst_evh_5048434852698054_completed.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7525385716635984_completed_gqsgb4uq5 = async (event, scope) => {
    return await actions.inst_evh_7525385716635984_completed_gqsgb4uq5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7525385716635984_completed_bsdpwl8lu = async (event, scope) => {
    return await actions.inst_evh_7525385716635984_completed_bsdpwl8lu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7525385716635984_completed_bv5nbf2dl = async (event, scope) => {
    return await actions.inst_evh_7525385716635984_completed_bv5nbf2dl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7525385716635984_completed = async (event, scope) => {
    return await actions.inst_evh_7525385716635984_completed.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_success_lh6r419cr = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_success_lh6r419cr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_success_su231jtei = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_success_su231jtei.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_success_rxx70gl94 = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_success_rxx70gl94.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_submit_zu8rz54ij = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_submit_zu8rz54ij.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_submit = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_5zxqkyh1s = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_5zxqkyh1s.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    card
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="DunningLetter"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        :color="$config.dunningLetter.color"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
        <strong class="text-muted me-2" v-if="!isModalComponent && item">
          {{ gettext('Dunning letter') }} - {{ item.reference_number }}
        </strong>
        <div class="btn-toolbar flex-nowrap gap-2">
          <ItpButton
            variant="light"
            icon="fa-sync-alt"
            :text="gettext('Reload')"
            :spinning="actions.reloadData.isRunning"
            @click.prevent.stop="
              evh_4440454779651433_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <ItpButton
            type="submit"
            form="u1w012sjv6"
            variant="primary"
            icon="fa-save"
            :text="gettext('Save')"
            :spinning="actions.save.isRunning"
            :disabled="!forms.dunningLetter || !forms.dunningLetter.submittable"
          >
          </ItpButton>
          <ItpButton
            variant="danger"
            icon="fa-trash"
            :text="gettext('Delete')"
            :spinning="actions.delete.isRunning"
            v-if="item.is_deletable"
            @click.prevent.stop="
              evh_2996188254223402_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <BDropdown no-caret ref="sendMailDropdown">
            <template #button-content>
              <div class="text-nowrap">
                <ItpIcon name="fa-envelope" class="me-2"> </ItpIcon>
                <span>
                  {{ gettext('Send email') }}
                </span>
                <ItpIcon name="fa-caret-down" class="ms-2"> </ItpIcon>
              </div>
            </template>
            <BDropdownForm :style="b_1024078196022266_style">
              <ItpFormGroup
                name="email"
                :label="gettext('Recipient')"
                :description="
                  gettext('Use semicolon to separate multiple addresses.')
                "
              >
                <ItpFormInput
                  type="text"
                  name="email"
                  :placeholder="gettext('Email address')"
                  size="md"
                  v-model="sendmail.email"
                  :value="sendmail.email"
                  @input="
                    !($event && typeof $event.stopPropagation === 'function') &&
                    (sendmail.email =
                      $event && typeof $event === 'object' && 'data' in $event
                        ? $event.data
                        : $event)
                  "
                >
                </ItpFormInput>
              </ItpFormGroup>
              <ItpFormGroup name="template" :label="gettext('Template')">
                <ItpFormSelect2
                  name="template"
                  size="md"
                  v-model="sendmail.template"
                  :value="sendmail.template"
                  @input="
                    !($event && typeof $event.stopPropagation === 'function') &&
                    (sendmail.template =
                      $event && typeof $event === 'object' && 'data' in $event
                        ? $event.data
                        : $event)
                  "
                  required
                  :options="emailTemplates"
                  valueField="id"
                  textField="name"
                >
                </ItpFormSelect2>
              </ItpFormGroup>
              <JobHandlerControl
                jobType="dunning_letters:email"
                :payload="
                  () => ({
                    id: item.id,
                    template: sendmail.template,
                    mail_to: _.chain(_.split(sendmail.email, ';'))
                      .map(_.trim)
                      .filter((p) => !!p)
                      .value(),
                  })
                "
                :text="pgettext('DunningLetter|Submit', 'Send')"
                icon="fa-envelope"
                block
                @completed="evh_5048434852698054_completed($event, {})"
              >
              </JobHandlerControl>
            </BDropdownForm>
          </BDropdown>
          <BDropdown no-caret ref="generatePdfDropdown">
            <template #button-content>
              <div class="text-nowrap">
                <ItpIcon name="fa-file-pdf" class="me-2"> </ItpIcon>
                <span>
                  {{ gettext('Generate document') }}
                </span>
                <ItpIcon name="fa-caret-down" class="ms-2"> </ItpIcon>
              </div>
            </template>
            <BDropdownForm :style="b_3061418626715996_style">
              <ItpFormGroup name="template" :label="gettext('Template')">
                <ItpFormSelect2
                  name="template"
                  :placeholder="gettext('Template')"
                  size="md"
                  v-model="printdoc.template"
                  :value="printdoc.template"
                  @input="
                    !($event && typeof $event.stopPropagation === 'function') &&
                    (printdoc.template =
                      $event && typeof $event === 'object' && 'data' in $event
                        ? $event.data
                        : $event)
                  "
                  required
                  :options="htmlTemplates"
                  valueField="id"
                  textField="name"
                >
                </ItpFormSelect2>
              </ItpFormGroup>
              <JobHandlerControl
                jobType="dunning_letters:pdf_generation"
                :payload="() => ({ id: item.id, template: printdoc.template })"
                :text="pgettext('DunningLetter|Submit', 'Generate document')"
                icon="fa-file-pdf"
                block
                @completed="evh_7525385716635984_completed($event, {})"
              >
              </JobHandlerControl>
            </BDropdownForm>
          </BDropdown>
        </div>
        <ItpResourceMeta :item="item" class="ms-auto" v-if="item">
        </ItpResourceMeta>
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <div class="row">
        <div class="col-7">
          <ItpForm
            name="dunningLetter"
            id="u1w012sjv6"
            v-if="item && item.id"
            @submit="evh_7789867021953041_submit($event, {})"
          >
            <div class="row">
              <ItpFormGroup
                name="status"
                :label="gettext('Status')"
                class="col-md-4"
              >
                <ItpFormSelect2
                  name="status"
                  size="md"
                  v-model="item.status"
                  :value="item.status"
                  @input="
                    !($event && typeof $event.stopPropagation === 'function') &&
                    (item.status =
                      $event && typeof $event === 'object' && 'data' in $event
                        ? $event.data
                        : $event)
                  "
                  :options="b_3273883411533724_options"
                >
                </ItpFormSelect2>
              </ItpFormGroup>
              <ItpFormGroup
                name="severity"
                :label="gettext('Severity')"
                class="col-md-2"
              >
                <ItpFormInput
                  name="severity"
                  size="md"
                  type="number"
                  :min="1"
                  :max="3"
                  v-model="item.severity"
                  :value="item.severity"
                  @input="
                    !($event && typeof $event.stopPropagation === 'function') &&
                    (item.severity =
                      $event && typeof $event === 'object' && 'data' in $event
                        ? $event.data
                        : $event)
                  "
                >
                </ItpFormInput>
              </ItpFormGroup>
            </div>
            <ItpFormGroup name="notes" :label="gettext('Notes')">
              <ItpFormTextarea
                name="notes"
                size="md"
                v-model="item.notes"
                :value="item.notes"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.notes =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormTextarea>
            </ItpFormGroup>
            <ItpFormGroup name="name" :label="gettext('Client')">
              <ItpFormInput
                name="name"
                size="md"
                v-model="item.customer_name"
                :value="item.customer_name"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.customer_name =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                readonly
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup name="address" :label="gettext('Address')">
              <ItpFormInput
                name="address"
                size="md"
                v-model="item.customer_data.address.address"
                :value="item.customer_data.address.address"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.customer_data.address.address =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                readonly
              >
              </ItpFormInput>
            </ItpFormGroup>
            <div class="row">
              <ItpFormGroup :cols-md="4">
                <ItpFormInput
                  name="zipcode"
                  size="md"
                  v-model="item.customer_data.address.postal_code"
                  :value="item.customer_data.address.postal_code"
                  @input="
                    !($event && typeof $event.stopPropagation === 'function') &&
                    (item.customer_data.address.postal_code =
                      $event && typeof $event === 'object' && 'data' in $event
                        ? $event.data
                        : $event)
                  "
                  readonly
                >
                </ItpFormInput>
              </ItpFormGroup>
              <ItpFormGroup :cols-md="8">
                <ItpFormInput
                  name="city"
                  size="md"
                  v-model="item.customer_data.address.city"
                  :value="item.customer_data.address.city"
                  @input="
                    !($event && typeof $event.stopPropagation === 'function') &&
                    (item.customer_data.address.city =
                      $event && typeof $event === 'object' && 'data' in $event
                        ? $event.data
                        : $event)
                  "
                  readonly
                >
                </ItpFormInput>
              </ItpFormGroup>
            </div>
          </ItpForm>
          <div class="form-group">
            <label>
              {{ gettext('Invoices') }}
            </label>
            <ItpTable
              :rows="
                item.items.length > 1
                  ? item.items.concat([
                      {
                        $rowClass: 'fw-bold text-muted',
                        invoice_number: 'Total',
                        invoice_data: {
                          amount: $fn._.sumBy(
                            item.items,
                            (p) => p.invoice_data.amount
                          ),
                          paid: $fn._.sumBy(
                            item.items,
                            (p) => p.invoice_data.paid
                          ),
                          debt: $fn._.sumBy(
                            item.items,
                            (p) => p.invoice_data.debt
                          ),
                        },
                      },
                    ])
                  : item.items
              "
              :columns="b_2771751501651205_columns"
              v-if="item"
            >
            </ItpTable>
          </div>
        </div>
        <div class="col-5">
          <div class="container-fluid" v-if="!item.jobs.length">
            <div class="text-center mt-2" role="alert">
              {{ gettext('No events') }}
            </div>
          </div>
          <div class="container-fluid" v-if="item.jobs.length">
            <div
              v-for="job in $fn._.orderBy(item.jobs, ['number'], ['desc'])"
              :key="job.id"
              class="row"
            >
              <div class="col-4 text-end pb-5 text-nowrap text-truncate">
                <div class="text-nowrap">
                  #{{ job.number }}
                  <ItpIcon v-if="job.job_type === 'email'" name="fa-envelope">
                  </ItpIcon>

                  <ItpIcon
                    v-if="job.job_type === 'document'"
                    name="fa-file-pdf"
                  >
                  </ItpIcon>
                </div>

                <div
                  class="text-muted text-nowrap"
                  :title="datetime(job.created_at)"
                >
                  <ItpIcon name="fa-clock"></ItpIcon>
                  {{ job.created_at | datetime({ distanceToNow: true }) }}
                </div>

                <div class="text-muted text-nowrap">
                  {{ username(job.created_by) }}
                </div>
              </div>

              <div class="col-8 border-start pb-5 text-nowrap text-truncate">
                <div>
                  <!--span :class="jobStatusBadgeClass(job.status)">
        {{pgettext('TaskRunStatus', job.status) }}
      </span-->

                  <span
                    class="ms-1 text-muted text-nowrap"
                    v-if="job.updated_at"
                    :title="datetime(job.updated_at)"
                  >
                    {{ job.updated_at | datetime({ distanceToNow: true }) }}
                  </span>
                </div>

                <div
                  v-if="job.job_type === 'email'"
                  class="text-truncate"
                  :title="job.mail_to"
                >
                  <span class="text-muted me-1">
                    {{ pgettext('email', 'To') }}:
                  </span>
                  {{
                    $fn._.isArray(job.mail_to)
                      ? job.mail_to.join('; ')
                      : job.mail_to
                  }}
                </div>

                <div
                  v-if="job.job_type === 'email' && job.subject"
                  class="text-truncate"
                  :title="job.subject"
                >
                  <span class="text-muted me-1">
                    {{ pgettext('email', 'Subject') }}:
                  </span>
                  {{ job.subject }}
                </div>

                <div
                  v-if="job.job_type === 'email' && job.tracking"
                  :title="datetime(job.tracking.timestamp)"
                >
                  <ItpIcon name="fa-eye"></ItpIcon>
                  <span class="ms-2">
                    {{
                      job.tracking.timestamp | datetime({ distanceToNow: true })
                    }}
                  </span>
                </div>

                <hr
                  v-if="job.log_url || job.pdf_url || job.html_url"
                  class="my-2"
                />

                <div v-if="job.status === 'failed'">
                  <ItpLink
                    :href="job.log_url"
                    icon="fa-file-alt"
                    :text="$i18n._('Error log')"
                    :tooltip="job.log_filename"
                    target="_blank"
                    class="d-block"
                    use-auth
                  ></ItpLink>
                </div>

                <div v-if="job.status === 'completed'">
                  <ItpLink
                    v-if="job.pdf_url"
                    :href="job.pdf_url"
                    icon="fa-file-pdf"
                    text="PDF"
                    :tooltip="job.pdf_filename"
                    target="_blank"
                    class="d-block"
                    use-auth
                  ></ItpLink>

                  <ItpLink
                    v-if="job.html_url"
                    :href="job.html_url"
                    icon="fa-file-code"
                    text="HTML"
                    :tooltip="job.html_filename"
                    target="_blank"
                    class="d-block"
                    use-auth
                  ></ItpLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="modal && loaded">
        <ItpButton
          :text="pgettext('DialogButton', 'Close')"
          variant="default"
          :disabled="actions.save && actions.save.isRunning"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
